import React from "react";
import { Formik, Form, Field } from "formik";
import { useParams } from "react-router-dom";
import { metaTags } from "../../utils/metaTags"; // Import predefined meta tags
import AddMeta from "./AddMeta";
import AIGenerateAction from "../Common/AIGenerateAction";
import SingleFieldAIGenerator from "../Common/SingleFieldAIGenerator";
import AIGeneratorConfig from "../Common/AIGeneratorConfig";
import { Button, Tooltip } from "antd";
import { SettingOutlined, ThunderboltOutlined } from "@ant-design/icons";

const MetaTag = ({ values, errors, touched, setFieldValue, courseId }) => {
  const { id } = useParams();
  const activeId = courseId || id;
  const [configVisible, setConfigVisible] = React.useState(false);
  const upperCaseName = (value) => {
    return value
      .split(/[:_]/) // Split by ':' or '_' into an array
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1)) // Capitalize each part
      .join(" "); // Join them with spaces
  };
  const lowerCaseName = (value) => {
    return value
      .split(/[:_]/) // Split by ':' or '_' into an array
      .map((part) => part.toLowerCase()) // Capitalize each part
      .join(" "); // Join them with spaces
  };
  return (
    <>
      <div className="d-flex align-items-center">
        <h4 className="mr-2 mb-0">Tags</h4>
        <div className="ms-auto d-flex align-items-center" style={{ gap: '8px' }}>
          <Tooltip title="Global SEO Settings">
            <Button 
              type="text"
              size="large"
              icon={<SettingOutlined style={{ fontSize: '18px' }} />} 
              onClick={() => setConfigVisible(true)}
              style={{ color: '#8c8c8c' }}
            />
          </Tooltip>
          {activeId && values.meta && values.meta.length > 0 && (
              <SingleFieldAIGenerator
                  mode="bulk"
                  courseId={activeId}
                  category="SEO"
                  activeMetaKeys={values.meta.map(m => m.name || m.property || m.httpEquiv).filter(Boolean)}
                  defaultSelectedKeys={values.meta.map(m => m.name || m.property || m.httpEquiv).filter(Boolean)}
                  onApply={(parsedJson) => {
                      if (parsedJson && typeof parsedJson === 'object') {
                          const updatedMetaKeys = Object.keys(parsedJson);
                          const newMeta = [...values.meta];
                          newMeta.forEach((metaTag, idx) => {
                              const key = metaTag.name || metaTag.property || metaTag.httpEquiv;
                              const matchingKey = updatedMetaKeys.find(k => k.toLowerCase() === key.toLowerCase());
                              if (matchingKey && typeof parsedJson[matchingKey] === 'string') {
                                  newMeta[idx].content = parsedJson[matchingKey];
                              }
                          });
                          setFieldValue("meta", newMeta);
                      }
                  }}
                  buttonComponent={
                      <Button
                          type="primary"
                          icon={<ThunderboltOutlined />}
                          style={{
                              background: 'linear-gradient(135deg, #6366F1 0%, #A855F7 100%)',
                              border: 'none',
                              boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.2)',
                              fontWeight: 500,
                              borderRadius: '6px',
                              height: '38px',
                              padding: '0 16px'
                          }}
                      >
                          Bulk Generate
                      </Button>
                  }
              />
          )}

          <AddMeta
            metaTags={metaTags} // Pass predefined meta tags
            values={values} // Pass Formik values
          />
        </div>
      </div>
      <hr />

      <div className="mt-4 mx-3">
        {/* <h5>Selected Meta Tags:</h5> */}
        <div className="row mx-1">
          {values.meta.map((metaTag, index) => (
            <div key={index} className="col-6 mb-4">
              <div className="input-block mb-1">
                <label className="col-form-label pb-0 pt-1">
                  {upperCaseName(
                    metaTag.name || metaTag.property || metaTag.httpEquiv
                  )}
                </label>
              </div>
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <div className="flex-grow-1">
                  <Field
                    name={`meta[${index}].content`}
                    placeholder={`Enter ${lowerCaseName(
                      metaTag.name || metaTag.property || metaTag.httpEquiv
                    )}`}
                    className="form-control"
                    style={{ height: '38px' }}
                  />
                </div>
                
                {/* AI Generation for all meta tags */}
                {activeId && (
                  <div style={{ flexShrink: 0 }}>
                    <SingleFieldAIGenerator
                        courseId={activeId}
                        category="SEO"
                        mode="single"
                        activeMetaKeys={values.meta.map(m => m.name || m.property || m.httpEquiv).filter(Boolean)}
                        fieldName={metaTag.name || metaTag.property || metaTag.httpEquiv}
                        fieldLabel={upperCaseName(metaTag.name || metaTag.property || metaTag.httpEquiv)}
                        currentValue={values.meta[index].content}
                        onApply={(parsedJson) => {
                            if (parsedJson && typeof parsedJson === 'object') {
                                const key = metaTag.name || metaTag.property || metaTag.httpEquiv;
                                const matchingKey = Object.keys(parsedJson).find(k => k.toLowerCase() === key.toLowerCase());
                                if (matchingKey && typeof parsedJson[matchingKey] === 'string') {
                                    setFieldValue(`meta[${index}].content`, parsedJson[matchingKey]);
                                }
                            }
                        }}
                    />
                  </div>
                )}

                {/* Delete Button */}
                <button
                  type="button"
                  className="btn btn-danger"
                  style={{
                    height: "38px",
                    width: "38px",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    borderRadius: "6px"
                  }}
                  onClick={() => {
                    const updatedMetaTags = [...values.meta];
                    updatedMetaTags.splice(index, 1);
                    setFieldValue("meta", updatedMetaTags);
                  }}
                  disabled={["title", "description"].includes(metaTag.name)}
                >
                  X
                </button>
              </div>
              {touched.meta?.[index]?.content &&
                errors.meta?.[index]?.content && (
                  <div className="error-msg text-danger mt-1">
                    {upperCaseName(
                      metaTag.name || metaTag.property || metaTag.httpEquiv
                    )}{" "}
                    {errors.meta[index].content}
                  </div>
                )}
            </div>
          ))}
        </div>
      </div>
      <AIGeneratorConfig
        visible={configVisible}
        onClose={() => setConfigVisible(false)}
        category="SEO"
        defaultPrompt="You are an expert SEO copywriter and marketer. Your goal is to generate compelling, accurate, and concise content."
        contextInstructions="Generate SEO meta content from course context. Output format and keys are enforced by runtime contract."
      />
    </>
  );
};

export default MetaTag;

